import React from 'react'
import Link from 'next/link'

import { Icon } from '@wartek-id/icon'
import { Text } from '@wartek-id/text'

import placeholderImage from 'public/images/toolkits/toolkit-img-placeholder.png'
import Image from './Image'

import type { TProfilePerangkatAjarItem } from 'types/profile'

type TToolkitCardProps = React.HTMLAttributes<HTMLAnchorElement> & {
  data?: TProfilePerangkatAjarItem
}

function ToolkitCard({ data, ...props }: TToolkitCardProps) {
  const isBuku = data?.toolkit_type?.includes('Buku')
  const isModulBahanAjar = data?.toolkit_type?.includes('Ajar')
  const isModulProjek = data?.toolkit_type === 'Modul Projek'

  const isToolkitContainVideo = data?.toolkit_meetings?.some((meeting) => {
    const meetingSupportMaterials = [
      ...(meeting?.references ?? []),
      ...(meeting?.exercises ?? []),
      ...(meeting?.reflection_instruments ?? []),
    ]
    return meetingSupportMaterials.some((ref) =>
      ['youtube url', 'rumah belajar']?.includes?.(ref?.type)
    )
  })

  const subtitleText = React.useMemo(() => {
    const classesText = data?.classes?.join(' • ')
    let subtitle
    if (isModulProjek) subtitle = `Fase ${data?.phase}`
    if (isBuku) subtitle = `${data?.toolkit_type} • ${classesText}`
    if (isModulBahanAjar) subtitle = `${data?.toolkit_type} • ${classesText}`

    return subtitle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const cpText = React.useMemo(() => {
    let CP = ''
    if (isModulBahanAjar && data?.domains?.length > 0)
      CP = `${data?.domains?.join(', ')}`
    if (isModulProjek && data?.subject) CP = `${data?.subject}`
    return CP
  }, [data, isModulBahanAjar, isModulProjek])

  const link = isBuku
    ? `/perangkat-ajar/books/${data?.id}`
    : `/perangkat-ajar/toolkits/${data?.id}`

  return (
    <section className="relative flex h-[170px] w-full cursor-pointer flex-row rounded-[4px] border border-gray-20 bg-white ">
      <Link href={link} passHref rel="noreferrer">
        <a
          className="relative flex flex-1 gap-x-4 p-4 hover:bg-surface-subdued"
          {...props}
        >
          <span className="flex-shrink-0 cursor-pointer">
            <Image
              style={{ borderRadius: '4px' }}
              src={data?.image_url}
              fallback={placeholderImage}
              width={64}
              height={85}
              alt={data?.title}
            />
          </span>
          <span className="flex flex-1 flex-col gap-y-1">
            <Text color="disabled" variant="helper">
              {subtitleText}
            </Text>
            <Text className="mb-1 cursor-pointer !font-semibold line-clamp-2">
              {data?.title}
            </Text>
            {!isBuku && (
              <Text className="mb-1 text-ellipsis !text-xs line-clamp-2">
                {cpText}
              </Text>
            )}
            {data?.special_need_tags && (
              <Text className="mb-1 text-ellipsis !text-xs line-clamp-1">
                {data?.special_need_tags?.join(', ')}
              </Text>
            )}
            {isToolkitContainVideo && (
              <div className="mb-1 flex items-center gap-x-1">
                <Icon className="!text-[16px]" color="disabled">
                  ondemand_video
                </Icon>
                <Text color="disabled" variant="helper">
                  Bervideo
                </Text>
              </div>
            )}
          </span>
        </a>
      </Link>
      <div
        className={
          'absolute bottom-4 right-4 flex items-center justify-end gap-x-3'
        }
      >
        <span className="flex gap-x-2">
          <Icon className="!text-[16px]" color="disabled">
            thumb_up
          </Icon>
          <Text variant="helper" color="disabled">
            {data?.like_count}
          </Text>
        </span>
        <span className="flex gap-x-2">
          <Icon className="!text-[16px]" color="disabled">
            get_app
          </Icon>
          <Text variant="helper" color="disabled">
            {data?.download_count}
          </Text>
        </span>
      </div>
    </section>
  )
}

export default ToolkitCard
